import {
  ActiveActionItemStatuses,
  ActiveIssueStatuses,
  ActiveTaskItemStatuses,
  DEFAULT_DRAWER_WIDTH,
  IssuesFilterByOption,
  IssuesSortByOption,
  MetricsFilterByOption,
  SortByDirection,
  TaskItemsFilterByOption,
  TaskItemsSortByOption,
  TeamIssuesGroupByOption,
  TeamStrategyFilterByOption,
  TeamTaskItemsGroupByOption,
  UserFeedbackFilterByOption,
  UserFeedbackGroupByOption,
  UserFeedbackTab,
  UserIssuesGroupByOption,
  UserStrategyFilterByOption,
  UserStrategyGroupByOption,
  UserStrategySortByOption,
  UserTaskItemsGroupByOption,
} from "src/constants";
import {
  IDataGridSettings,
  IMetricsSettings,
  ISettingsData,
  ITableSettings,
  ITeamImplementationSettings,
  ITeamMeetingSettings,
  ITeamStrategySettings,
  IUserImplementationSettings,
  IUserMeetingsSettings,
  IUserStrategySettings,
  type IUserFeedbackSettings,
} from "src/hooks";

const DEFAULT_SETTINGS_DATA: ISettingsData = {
  homeSettings: {
    showPeople: false,
    selectedTeamGuids: [],
  },
  sideMenuSettings: {
    isExpanded: false,
    expandedWidth: 240,
  },
  drawerSettings: {
    width: DEFAULT_DRAWER_WIDTH,
  },
  chatBotSettings: {
    width: 640,
    height: 600,
  },
  teamOverviewSettings: {
    expandedSection: undefined,
    networkListView: false,
    networkListViewFilterByOption: undefined,
  },
  teamStrategySettings: {},
  teamImplementationSettings: {},
  teamMetricsSettings: {},
  teamMeetingSettings: {},
  userStrategySettings: {},
  userImplementationSettings: {},
  userMetricsSettings: {},
  userMeetingsSettings: {},
  userFeedbackSettings: {},
  searchSettings: {
    searchHistory: [],
  },
  featureFlags: {
    surveys: false,
    snowfall: false,
  },
  dataGridSettings: {},
  tableSettings: {},
  coherenceReportSettings: {
    selectedTeamGuids: [],
    comparisonTeamGuids: undefined,
  },
  dismissedMeetingNotifications: [],
};

const DEFAULT_USER_STRATEGY_SETTINGS: IUserStrategySettings = {
  tableView: true,
  groupByOption: UserStrategyGroupByOption.Team,
  filterByOption: UserStrategyFilterByOption.All,
  filterByStatusOptions: null,
  sortByOption: UserStrategySortByOption.DateModified,
  sortByDirection: SortByDirection.Descending,
};

const DEFAULT_USER_IMPLEMENTATION_SETTINGS: IUserImplementationSettings = {
  tableView: true,
  taskItemsGroupByOption: UserTaskItemsGroupByOption.InFocus,
  taskItemsFilterByOption: TaskItemsFilterByOption.Status,
  taskItemsFilterByStatusOptions: ActiveTaskItemStatuses,
  taskItemsSortByOption: TaskItemsSortByOption.DateCreated,
  taskItemsSortByDirection: SortByDirection.Descending,
  issuesGroupByOption: UserIssuesGroupByOption.None,
  issuesFilterByOption: IssuesFilterByOption.Status,
  issuesFilterByStatusOptions: ActiveIssueStatuses,
  issuesSortByOption: IssuesSortByOption.Upvotes,
  issuesSortByDirection: SortByDirection.Descending,
};

const DEFAULT_TEAM_STRATEGY_SETTINGS: ITeamStrategySettings = {
  tableView: true,
  filters: {
    [TeamStrategyFilterByOption.Owner]: [],
    [TeamStrategyFilterByOption.Status]: ActiveActionItemStatuses.map((status) => status.toString()),
  },
};

const DEFAULT_TEAM_IMPLEMENTATION_SETTINGS: ITeamImplementationSettings = {
  tableView: true,
  taskItemsGroupByOption: TeamTaskItemsGroupByOption.None,
  taskItemsFilterByOption: TaskItemsFilterByOption.Status,
  taskItemsFilterByStatusOptions: ActiveTaskItemStatuses,
  taskItemsSortByOption: TaskItemsSortByOption.DateCreated,
  taskItemsSortByDirection: SortByDirection.Descending,
  issuesGroupByOption: TeamIssuesGroupByOption.None,
  issuesFilterByOption: IssuesFilterByOption.Status,
  issuesFilterByStatusOptions: ActiveIssueStatuses,
  issuesSortByOption: IssuesSortByOption.Upvotes,
  issuesSortByDirection: SortByDirection.Descending,
};

const DEFAULT_METRICS_SETTINGS: IMetricsSettings = {
  filters: {
    [MetricsFilterByOption.Team]: [],
    [MetricsFilterByOption.ReportingPerson]: [],
    [MetricsFilterByOption.Tag]: [],
  },
};

const DEFAULT_DATAGRID_SETTINGS: IDataGridSettings = {
  columnVisibility: {},
};

const DEFAULT_TABLE_SETTINGS: ITableSettings = {
  columnVisibility: {},
};

const DEFAULT_TEAM_MEETING_SETTINGS: ITeamMeetingSettings = {
  metrics: DEFAULT_METRICS_SETTINGS,
  strategy: DEFAULT_TEAM_STRATEGY_SETTINGS,
};

const DEFAULT_USER_MEETINGS_SETTINGS: IUserMeetingsSettings = {
  filterValue: undefined,
};

const DEFAULT_USER_FEEDBACK_SETTINGS: IUserFeedbackSettings = {
  tab: UserFeedbackTab.Received,
  receivedFeedbackGroupByOption: UserFeedbackGroupByOption.State,
  receivedFeedbackFilters: {
    [UserFeedbackFilterByOption.FormName]: [],
    [UserFeedbackFilterByOption.Sender]: [],
    [UserFeedbackFilterByOption.Receiver]: [],
    [UserFeedbackFilterByOption.RequiresReviewInOneOnOne]: [],
    [UserFeedbackFilterByOption.ReviewedInOneOnOne]: [],
    [UserFeedbackFilterByOption.ExcludeDrafts]: [],
  },
  givenFeedbackGroupByOption: UserFeedbackGroupByOption.State,
  givenFeedbackFilters: {
    [UserFeedbackFilterByOption.FormName]: [],
    [UserFeedbackFilterByOption.Sender]: [],
    [UserFeedbackFilterByOption.Receiver]: [],
    [UserFeedbackFilterByOption.RequiresReviewInOneOnOne]: [],
    [UserFeedbackFilterByOption.ReviewedInOneOnOne]: [],
    [UserFeedbackFilterByOption.ExcludeDrafts]: [],
  },
};

export {
  DEFAULT_DATAGRID_SETTINGS,
  DEFAULT_DRAWER_WIDTH,
  DEFAULT_METRICS_SETTINGS,
  DEFAULT_SETTINGS_DATA,
  DEFAULT_TABLE_SETTINGS,
  DEFAULT_TEAM_IMPLEMENTATION_SETTINGS,
  DEFAULT_TEAM_MEETING_SETTINGS,
  DEFAULT_TEAM_STRATEGY_SETTINGS,
  DEFAULT_USER_FEEDBACK_SETTINGS,
  DEFAULT_USER_IMPLEMENTATION_SETTINGS,
  DEFAULT_USER_MEETINGS_SETTINGS,
  DEFAULT_USER_STRATEGY_SETTINGS,
};
